import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import CosmeticsShop from './CosmeticsShop';
import Game from './Game';
import Leaderboard from './Leaderboard';
import Lobby from './Lobby';
import OnlineGame from './OnlineGame';
import HowToPlay from './HowToPlay';
import AuthScreen from './AuthScreen';
import { requestNotificationPermission, onForegroundMessage } from './notifications';
import { SplashScreen } from '@capacitor/splash-screen';

function App() {
  const { user, profile, logout } = useAuth();
  const [showShop, setShowShop] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showLobby, setShowLobby] = useState(false);
  const [onlineGameId, setOnlineGameId] = useState(null);
  const [gameInProgress, setGameInProgress] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showGuestLimit, setShowGuestLimit] = useState(false);

  const handleGameStateChange = useCallback((inProgress) => {
    setGameInProgress(inProgress);
  }, []);

  // Push notifications
  useEffect(() => {
    if (user && !user.isAnonymous) {
      requestNotificationPermission(user.uid);
      const unsubscribe = onForegroundMessage((payload) => {
        console.log('Foreground message:', payload);
        alert(`🃏 ${payload.notification?.title}: ${payload.notification?.body}`);
      });
      return () => unsubscribe();
    }
  }, [user]);

  // Guest game limit
  useEffect(() => {
    if (user?.isAnonymous) {
      const guestGames = parseInt(localStorage.getItem('guestGames') || '0');
      if (guestGames >= 5) setShowGuestLimit(true);
    }
  }, [user]);

  // Splash screen
  useEffect(() => {
    const hideSplash = async () => {
      try {
        await SplashScreen.hide({ fadeOutDuration: 500 });
      } catch (e) {}
    };
    hideSplash();
  }, []);

  if (!user) {
    return <AuthScreen />;
  }

  // Online game in progress
  if (onlineGameId) {
    return <OnlineGame gameId={onlineGameId} onLeave={() => setOnlineGameId(null)} />;
  }

  // Lobby
  if (showLobby) {
    return <Lobby onGameStart={(id) => { setShowLobby(false); setOnlineGameId(id); }} onBack={() => setShowLobby(false)} />;
  }

  const backLabel = gameInProgress ? 'Back to game' : 'Back';

  const overlayHeader = (onClose) => (
    <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 14, color: '#888' }}>Welcome, {profile?.displayName}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>{backLabel}</button>
        <button onClick={logout} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '6px 14px', fontSize: 13, cursor: 'pointer' }}>Sign out</button>
      </div>
    </div>
  );

  return (
    <div style={{ fontFamily: 'sans-serif' }}>
      <Game
        onOpenShop={() => setShowShop(true)}
        onOpenLeaderboard={() => setShowLeaderboard(true)}
        onOpenOnline={() => setShowLobby(true)}
        onOpenHowToPlay={() => setShowHowToPlay(true)}
        onGameStateChange={handleGameStateChange}
        onSignOut={logout}
      />

      {showShop && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 50, overflowY: 'auto' }}>
          {overlayHeader(() => setShowShop(false))}
          <CosmeticsShop onClose={() => setShowShop(false)} />
        </div>
      )}

      {showLeaderboard && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 50, overflowY: 'auto' }}>
          {overlayHeader(() => setShowLeaderboard(false))}
          <Leaderboard onClose={() => setShowLeaderboard(false)} />
        </div>
      )}

      {showHowToPlay && (
        <div style={{ position: 'fixed', inset: 0, background: '#fff', zIndex: 50, overflowY: 'auto' }}>
          {overlayHeader(() => setShowHowToPlay(false))}
          <HowToPlay onClose={() => setShowHowToPlay(false)} />
        </div>
      )}

      {showGuestLimit && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', maxWidth: 340, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>👑</div>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 8 }}>You've played 5 free games!</div>
            <div style={{ fontSize: 14, color: '#888', marginBottom: 20, lineHeight: 1.6 }}>Create a free account to keep playing and unlock stats, cosmetics and the leaderboard!</div>
            <button onClick={() => { logout(); setShowGuestLimit(false); localStorage.removeItem('guestGames'); }} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer', width: '100%', marginBottom: 10 }}>
              Create free account
            </button>
            <button onClick={() => setShowGuestLimit(false)} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer', width: '100%' }}>
              Keep playing as guest
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
